import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/usePageTitle';
import { usePipelineRuns } from '@/hooks/usePipelineRuns';
import { formatDateTime } from '@/utils/format';

const statusColors: Record<string, string> = {
  QUEUED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300',
  RUNNING: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  SUCCEEDED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  PARTIAL: 'bg-yellow-100 text-yellow-800 dark:bg-amber-900/40 dark:text-amber-300',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  CANCELLED: 'bg-gray-100 text-gray-500 dark:bg-gray-900/40 dark:text-gray-400',
  ROLLED_BACK: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
};

export const PipelineRunsListPage = () => {
  usePageTitle('Pipeline Runs');
  const { data: runs, isLoading } = usePipelineRuns();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Pipeline Runs</CardTitle>
            <CardDescription>
              Automated improvement pipeline runs. Each run collects quality failures, diagnoses
              root causes, generates patches, and replays test cases.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {!runs?.length ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No pipeline runs yet. Trigger one from a flagged call or wait for the daily schedule.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>Configuration</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Stage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow
                    key={run.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/agent-improvement/runs/${run.id}`)}
                  >
                    <TableCell className="text-muted-foreground text-xs">
                      {formatDateTime(run.created_at)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {run.configuration_id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-xs">{run.trigger}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[run.status] ?? ''}>{run.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{run.stage}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
